import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import CabeceraSeccion from '../components/CabeceraSeccion';
import { useSite } from '../site/SiteProvider';
import { useTranslation } from 'react-i18next';
import { PREDIOS, HECTAREAS } from '../config/predios';
/* 🔴 SE IMPORTA LA HOJA DE LA FUNDACIÓN A PROPÓSITO (Faina, 30-ago: «se ve
   bastante distinto a lo que sería La Fundación, que podrían seguir un mismo
   estilo; ¿puedes hacer algo parecido?»).

   No se copian sus valores aquí: se USAN SUS CLASES. Copiarlos habría dejado
   dos hojas que empiezan iguales y se separan a la primera corrección —se
   arregla una y se olvida la otra—, que es exactamente el problema que él vio.
   Compartiéndola, cualquier ajuste de estilo en La Fundación llega solo aquí.

   ⚠️ Quien edite fundacion.css debe saber que la leen DOS páginas. */
import './fundacion.css';
import './conservacion.css';

/**
 * CONSERVACIÓN — la hoja de jeshua.cl y jeshua.es (Definición 74, 30-ago-2026).
 *
 * Faina: «en el sitio .cl y .es vamos a agregar 1 hoja más que se va a llamar
 * conservación… lo más importante es que ninguna donación nos ate a nada y a
 * nadie. No queremos que esta fundación dependa de nada ni de nadie. Todas las
 * estructuras de poder y de control deben estar lejos.»
 *
 * Esa frase ordena la página entera: por eso el bloque de las ocho reglas no
 * está al final en letra pequeña, sino en medio y con el mismo peso que lo que
 * ofrecemos. Una fundación que dice de entrada lo que NO va a aceptar es más
 * creíble que una que solo cuenta lo que quiere recibir.
 *
 * ── LA FORMA ──────────────────────────────────────────────────────────────
 * Misma arquitectura que La Fundación, que es su hermana natural: las dos son
 * páginas donde la casa se explica. Rótulo gris en versales · frase de lectura
 * que carga el bloque · párrafo justificado debajo · y la regla naranja sobre
 * cada pilar. Hicieron falta dos intentos: primero copié el molde de la página
 * legal (columna estrecha, h1 propio), y después homologué solo el ancho — que
 * era la mitad del problema. Lo que se veía distinto no era la medida, era el
 * LENGUAJE.
 *
 * ── LO QUE ESTA PÁGINA NO PUEDE DECIR ─────────────────────────────────────
 * 🟥 Regla dura: todo lo que publicamos debe ser verdad, y el modo real de
 * fallar es poner una cifra de META donde se acreditan HECHOS. Hoy no hay ni un
 * predio bajo nuestro cuidado —las hectáreas de Faina son reales, pero su
 * donación no está formalizada—. Por eso «lo que ya cuidamos» LEE
 * `config/predios.js` en vez de llevar texto a mano: mientras la lista esté
 * vacía dice que no hay ninguno, y el día que entre el primero aparece solo.
 * Y el ciclo de trabajo va en futuro hasta la primera vez.
 *
 * 🔴 LA CONSERVACIÓN NO TIENE NADA QUE VER CON EL DINERO (Faina, 30-ago). Hubo
 * aquí una tercera «manera de dar» llamada «En dinero» y estaba mal: el dinero
 * es donación a la Fundación y tiene su propio sitio. El fondo de cuidado, que
 * sí es dinero, vive en su bloque y con su nombre.
 *
 * 🔴 EL TEXTO VA SEGÚN EL PAÍS. Las palabras salen de
 * `config/conservacion_paises.js` y ninguna se escribe a mano aquí.
 *
 * ⚠️⚠️ Como la página legal: esto no sustituye a un abogado.
 */

/* Los dos caminos, y los dos son de TIERRA. Primero el que menos nos
   compromete; al revés se lee como que lo que queremos es quedarnos con ella. */
/* Los dos caminos, y los dos son de TIERRA. Primero el que menos nos
   compromete; al revés se lee como que lo que queremos es quedarnos con ella.
   Desde el 13-sep (/traducir-web) los textos son CLAVES: el español vive en la
   tabla de textos. Donde entraba el diccionario por país (`T`), hay una clave
   por país, porque la frase entera cambia y se traduce entera. */
const caminosDe = (t) => [
  { rotulo: t('conserv.camino1_rotulo'), frase: t('conserv.camino1_frase'), texto: t('conserv.camino1_texto') },
  { rotulo: t('conserv.camino2_rotulo'), frase: t('conserv.camino2_frase'), texto: t('conserv.camino2_texto') }
];

/* El ciclo, de los Land Trust Standards and Practices — el estándar que siguen
   las organizaciones de conservación desde 1989. Los dos pasos que la gente no
   espera, la línea de base y el monitoreo, son los que separan conservar de
   decir que se conserva. Los pasos 2, 3 y 5 cambian con el país. */
const pasosDe = (t, pais) => [
  [t('conserv.paso1_t'), t('conserv.paso1_d')],
  [t('conserv.paso2_t'), t('conserv.paso2_d_' + pais)],
  [t('conserv.paso3_t'), t('conserv.paso3_d_' + pais)],
  [t('conserv.paso4_t'), t('conserv.paso4_d')],
  [t('conserv.paso5_t'), t('conserv.paso5_d_' + pais)],
  [t('conserv.paso6_t'), t('conserv.paso6_d')],
  [t('conserv.paso7_t'), t('conserv.paso7_d')],
  [t('conserv.paso8_t'), t('conserv.paso8_d')]
];


/* Las ocho reglas de la Def 74, literales. Son las mismas en los dos países: la
   independencia no depende de la jurisdicción. */
const reglasDe = (t) => [1, 2, 3, 4, 5, 6, 7, 8].map((n) => [t('conserv.regla' + n + '_t'), t('conserv.regla' + n + '_d')]);


const ConservacionPage = () => {
  const { t } = useTranslation();
  const sitio = useSite();
  const enChile = sitio.key === 'cl';
  const pais = enChile ? 'cl' : 'es';

  return (
    <Box className="fun cons">
      <CabeceraSeccion titulo={t('menu.conservacion')} />

      <Container maxWidth="lg">

        {/* ── TODO EN DOS COLUMNAS (Faina, 30-ago: «puedes trabajar todas las
            secciones en 2 columnas») ─────────────────────────────────────────

            Y los pares NO son arbitrarios: cada dos bloques se explican
            mirándose, igual que misión y propósito en La Fundación. Emparejar
            por relleno —lo que sobra al lado de lo que cabe— se nota enseguida
            y deja columnas de alturas muy distintas.

            Dos bloques van a ancho completo a propósito: el ciclo de trabajo,
            porque una lista de ocho partida por la mitad se lee mal, y la caja de
            las ocho reglas, porque es el corazón de la página y su jerarquía
            viene justo de ser el único elemento que ocupa todo el ancho. Los
            dos llevan su contenido interno en dos columnas, así que la página
            entera respira a dos. */}

        {/* ① EL PORQUÉ · y QUÉ SERÁ esa tierra */}
        <Box className="fun-seccion fun-dos cons-dos">
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.porque_rotulo')}</Typography>
            <Typography className="fun-frase">{t('conserv.porque_frase')}</Typography>
            <Typography className="fun-parrafo">{t('conserv.porque_texto')}</Typography>
          </Box>
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.parques_rotulo')}</Typography>
            <Typography className="fun-frase">{t('conserv.parques_frase')}</Typography>
            <Typography className="fun-parrafo">{t('conserv.parques_texto')}</Typography>
          </Box>
        </Box>

        {/* ⭐ LA ENSEÑANZA (Faina, 30-ago: «déjala como un mensaje
            entre comillas, a lo largo y a continuación»).

            Estaba dentro del párrafo de Los Parque Jeshua y ahí se perdía: es lo
            más fuerte de la página y estaba haciendo de última frase de un
            bloque a media columna. Sacada y a todo el ancho, respira.

            🔴 LAS COMILLAS NO SON ADORNO: en este sitio son un MARCADOR
            RESERVADO que significa «esto es canalizado» (Faina, 28-ago). Aquí es
            legítimo porque la frase es SUYA —él la escribió— y la comilla
            atribuye a un AUTOR, no a una entidad; y por eso tampoco lleva firma.
            La consecuencia, que rige para todo el sitio: no entrecomillar nunca
            texto explicativo ni de producto.

            Un solo par de comillas y no dos, porque él lo llamó «un mensaje» en
            singular y lo escribió de una vez. La regla de una comilla por frase
            es para aportes DISTINTOS agrupados, que no es el caso.

            ⚠️ ESTILO PRESTADO DE `.gr-afirmacion` (Agradecimientos), que es como
            habla la Fundación en su propio nombre. Los valores están copiados y
            eso es deuda consciente: si esta forma aparece una TERCERA vez, hay
            que sacarla a un estilo común en vez de repetirla. */}
        <Typography className="cons-linea cons-ensenanza">
          {t('conserv.ensenanza')}
        </Typography>

        {/* ② LAS DOS MANERAS DE DAR — el par original, y el que da la pauta */}
        <Box className="fun-seccion">
          <Typography className="fun-rotulo">{t('conserv.maneras_rotulo')}</Typography>
          <Box className="fun-dos cons-dos cons-dos-hijas">
            {caminosDe(t).map(({ rotulo, frase, texto }) => (
              <Box className="fun-pilar" key={rotulo}>
                <Typography className="fun-rotulo cons-rotulo-pilar">{rotulo}</Typography>
                <Typography className="fun-frase">{frase}</Typography>
                <Typography className="fun-parrafo">{texto}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* ③ QUIÉN ENTRA · CÓMO SE ABRE — las dos caras del acceso */}
        <Box className="fun-seccion fun-dos cons-dos">
          {/* 🔴 QUIÉN ENTRA, corregido dos veces el mismo día. Faina dijo
              primero «la comunidad serán todos los suscriptores» y yo lo
              entendí como el registro gratuito. No: «a los Parque Jeshua solo
              podrán acceder los que paguen los USD 33 de suscripción anual
              (mínimo)» — desde el plan 2, y también los de arriba. */}
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.quien_rotulo')}</Typography>
            {/* Arriba se queda la frase corta y el detalle baja al párrafo
                (Faina, 30-ago). Fixes técnicos sobre su texto: «todas LAS
                personas», «Fundación» con mayúscula como en todo el sitio, y el
                espacio doble. */}
            <Typography className="fun-frase">{t('conserv.quien_frase')}</Typography>
            <Typography className="fun-parrafo">{t('conserv.quien_texto')}</Typography>
          </Box>
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.abre_rotulo')}</Typography>
            <Typography className="fun-frase">{t('conserv.abre_frase')}</Typography>
            <Typography className="fun-parrafo">{t('conserv.abre_texto')}</Typography>
          </Box>
        </Box>

        {/* ④ EL CICLO DE TRABAJO — EN DOS COLUMNAS (Faina, 30-ago: «configúralo
            en 2 columnas para que quede homogéneo con el resto»).

            Era el único bloque con contenido propio a todo el ancho. Ahora sigue
            el patrón de «Las dos maneras»: rótulo de sección arriba y dos
            pilares debajo.

            ⭐ Y LA PARTICIÓN NO ES POR LA MITAD, ES POR EL MOMENTO: los cuatro
            primeros pasos ocurren ANTES de firmar —y cualquiera puede acabar en
            un no—; los tres últimos, DESPUÉS, cuando la tierra ya está a
            nuestro cuidado. Partido ahí, deja de ser una lista larga y pasan a
            ser dos ideas que se entienden solas. */}
        <Box className="fun-seccion">
          <Typography className="fun-rotulo">{t('conserv.compromiso_rotulo')}</Typography>
          <Box className="fun-dos cons-dos cons-dos-hijas">
            <Box className="fun-pilar">
              <Typography className="fun-rotulo cons-rotulo-pilar">{t('conserv.antes_rotulo')}</Typography>
              <Typography className="fun-frase">{t('conserv.antes_frase')}</Typography>
              <Box component="ol" className="cons-pasos">
                {pasosDe(t, pais).slice(0, 4).map(([tt, d]) => (
                  <li key={tt}><span className="cons-paso-t">{tt}</span>{d}</li>
                ))}
              </Box>
            </Box>
            <Box className="fun-pilar">
              <Typography className="fun-rotulo cons-rotulo-pilar">{t('conserv.despues_rotulo')}</Typography>
              <Typography className="fun-frase">{t('conserv.despues_frase')}</Typography>
              <Box component="ol" className="cons-pasos" start={5}>
                {pasosDe(t, pais).slice(4).map(([tt, d]) => (
                  <li key={tt}><span className="cons-paso-t">{tt}</span>{d}</li>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ⑤ LO QUE NO · CON QUÉ SE CUIDA — dos pilares que se responden */}
        <Box className="fun-seccion fun-dos cons-dos">
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.nunca_rotulo')}</Typography>
            {/* Sin punto final: es un sintagma, no una oración — así lo
                escribió Faina (30-ago). Las otras frases de la página sí lo
                llevan porque son oraciones completas. */}
            <Typography className="fun-frase">{t('conserv.nunca_frase')}</Typography>
            {/* 🔴 EN FUTURO, y cada una con su viñeta (Faina, 30-ago). El futuro
                es lo correcto mientras no haya ningún predio: en presente —«no la
                urbanizamos»— sonaba a que ya lo estamos haciendo. Y separar la
                venta de la garantía las convierte en dos promesas, que es lo que
                son. */}
            <Box component="ul" className="cons-lista">
              {[1, 2, 3, 4, 5, 6].map((n) => <li key={n}>{t('conserv.nunca_' + n)}</li>)}
            </Box>
          </Box>
          {/* Aquí SÍ hay dinero, y por eso va en su propio bloque y con su
              nombre: no es una vía de donación, es lo que sostiene la tierra ya
              aceptada. La distinción la marcó Faina el 30-ago. */}
          <Box className="fun-pilar">
            {/* La afirmación sube a rótulo y la etiqueta baja a frase (Faina,
                30-ago). Es al revés que en los otros pilares, y aquí funciona:
                lo que hay que oír primero es que cuidar cuesta, y lo de debajo
                presenta la lista de en qué. */}
            <Typography className="fun-rotulo">{t('conserv.costos_rotulo')}</Typography>
            <Typography className="fun-frase">{t('conserv.costos_frase')}</Typography>
            {/* Seis viñetas, las mismas que la columna de al lado (Faina,
                30-ago), y ORDENADAS POR MAGNITUD. Salen del apartado 7.2 del
                plan de trabajo — coste RECURRENTE, no inversión inicial.

                🔴 Faina preguntó si eran de verdad los principales, y la primera
                versión tenía dos errores: «plantar, regar y reponer» es sobre
                todo inversión de restauración —lo anual es el riego de los dos
                primeros veranos—, y «el estudio de cada año» mezclaba el
                inventario completo, que es CADA TRES AÑOS y es lo caro, con el
                informe anual, que es barato. Faltaba además atender a quien
                viene, que en un parque abierto sí es gasto de todos los años.

                Y el personal va primero porque es, con diferencia, el mayor: los
                demás juntos no suelen alcanzarlo. */}
            <Box component="ul" className="cons-lista">
              {/* ⚠️ CADA VIÑETA, UNA LÍNEA (Faina, 30-ago) — y en cualquier
                  pantalla, no solo en la ancha. Por eso son tan cortas como las
                  de la columna de al lado: en cuanto pasan de unos 40 caracteres
                  se parten en dos por debajo de 1.300 px. */}
              <li>{t('conserv.costos_1')}</li>
              <li>{t('conserv.costos_2')}</li>
              <li>{t('conserv.costos_3_' + pais)}</li>
              <li>{t('conserv.costos_4_' + pais)}</li>
              <li>{t('conserv.costos_5')}</li>
              <li>{t('conserv.costos_6')}</li>
            </Box>
          </Box>
        </Box>

        {/* ⑥ EL CORAZÓN — la única caja, a ancho completo y con sus ocho reglas
            en dos columnas. Su jerarquía viene precisamente de ser el único
            elemento que ocupa todo el ancho: si todo se destaca, nada destaca. */}
        <Box className="fun-seccion cons-libre">
          <Typography className="fun-rotulo">{t('conserv.libre_rotulo')}</Typography>
          <Typography className="fun-frase">{t('conserv.libre_frase')}</Typography>
          {/* Tratamiento propio, distinto del párrafo normal (Faina, 30-ago:
              «puede ser en otro tono, una letra un poco distinta»). Ver
              `.cons-libre-entrada` en conservacion.css. */}
          {/* En dos filas (Faina, 30-ago): la declaración de independencia va
              sola, y debajo lo que se sigue de ella. El punto y coma pasa a
              punto — separadas, son dos afirmaciones, no una con inciso. */}
          <Typography className="cons-libre-entrada">{t('conserv.libre_entrada_1')}</Typography>
          <Typography className="cons-libre-entrada cons-libre-entrada-2">{t('conserv.libre_entrada_2')}</Typography>
          <Box component="ul" className="cons-reglas">
            {/* 🔴 EL NOMBRE EN SU PROPIA LÍNEA (Faina, 30-ago). Antes iban
                seguidos —primero con guion, que él quitó, y después con punto—.
                Separados, el nombre deja de necesitar el punto: ya no es el
                arranque de una frase, es el rótulo de la regla. Y las ocho se
                pueden recorrer leyendo solo los nombres. */}
            {reglasDe(t).map(([tt, d]) => (
              <li key={tt}>
                <span className="cons-regla-t">{tt}</span>
                {d}
              </li>
            ))}
          </Box>
        </Box>

        {/* ⑦ LA LEY · LO QUE YA CUIDAMOS — lo que dice el derecho, y lo que
            decimos nosotros de nuestros propios hechos */}
        <Box className="fun-seccion fun-dos cons-dos">
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.ley_rotulo', { pais: t('conserv.pais_' + pais) })}</Typography>
            {enChile ? (
              <>
                {/* En una línea y en el registro del resto (Faina, 30-ago: «o
                    redactamos distinto para mantener el formato de otras
                    secciones»). Ninguna otra frase de la página usa dos puntos:
                    todas son afirmaciones simples, y esta lo es ahora. */}
                {/* ⚠️ «en tenerLA» no tenía antecedente: el rótulo dice «cómo
                    funciona en Chile» y no nombra ninguna figura, así que el
                    pronombre apuntaba a algo aún no dicho. Faina lo cazó con una
                    pregunta: «¿qué fue lo primero que tuvo Chile?». Mismo fallo
                    que «conocerla» en el ciclo, y misma cura: nombrar la cosa. */}
                <Typography className="fun-frase">{t('conserv.ley_cl_frase')}</Typography>
                {/* Puntuación revisada (Faina, 30-ago). Tres arreglos y un
                    hallazgo:
                    · La primera frase era un FRAGMENTO sin verbo —«El derecho
                      real … de 2016.»— y obligaba a leer dos veces. Ahora la ley
                      va como aposición entre comas y la frase tiene sujeto.
                    · Los DOS PUNTOS antes de «sigue vigente» no anunciaban nada:
                      lo que hay ahí es una consecuencia, y se dice con «por eso».
                    · Los dos puntos del segundo párrafo unían cosas que no se
                      explican entre sí. Punto y coma.
                    · 🔴 «ESE TRÁMITE» SE HABÍA QUEDADO SIN ANTECEDENTE: hablaba
                      de la inscripción en el Registro de Donatarias, que se cayó
                      en un recorte anterior. Se sustituye por lo que de verdad
                      importa al lector — qué beneficio le da. */}
                <Typography className="fun-parrafo">{t('conserv.ley_cl_p1')}</Typography>
                <Typography className="fun-parrafo cons-sigue">{t('conserv.ley_cl_p2')}</Typography>
              </>
            ) : (
              <>
                {/* En una línea y en el mismo registro que la de Chile, para
                    que las dos versiones del sitio se vean iguales. */}
                <Typography className="fun-frase">{t('conserv.ley_es_frase')}</Typography>
                <Typography className="fun-parrafo">{t('conserv.ley_es_p1')}</Typography>
                <Typography className="fun-parrafo cons-sigue">{t('conserv.ley_es_p2')}</Typography>
              </>
            )}
          </Box>

          {/* 🟥 EL BLOQUE QUE NO PUEDE MENTIR, y por eso no lleva texto a mano:
              lee config/predios.js. */}
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.cuidamos_rotulo')}</Typography>
            {PREDIOS.length === 0 ? (
              <>
                {/* 🟥 EL HECHO BAJA AL PÁRRAFO. La frase de carga decía «Todavía
                    ninguna hectárea» y Faina la cambió; pero ESA era la que
                    cumplía la regla dura —declarar que hoy no cuidamos ninguna—,
                    así que el hecho pasa a abrir el párrafo. No puede quedarse
                    fuera de la página mientras `PREDIOS` esté vacío. */}
                <Typography className="fun-frase">{t('conserv.cuidamos_vacio_frase')}</Typography>
                {/* Texto de Faina (30-ago), con tres fixes técnicos: la tilde de
                    «sí» adverbio, fuera la coma entre el verbo y su objeto
                    («informando, los avances»), y «Parque Jeshua» en mayúscula
                    como en el resto del sitio.

                    Y un cuarto que no es ortografía: él escribió «Les iremos
                    informando» y toda esta página tutea —«la tierra sigue siendo
                    tuya», «te diremos»—. Pasa a «Te iremos informando». */}
                <Typography className="fun-parrafo">{t('conserv.cuidamos_vacio_p1')}</Typography>
                {/* Este párrafo desarrolla la frase de carga —«estamos aprendiendo
                    para cuidar bien»— y por eso va aquí: dice CON QUIÉN se
                    aprende. Sin nombres de fundaciones ni programas mientras no
                    haya un convenio que se pueda enseñar. */}
                <Typography className="fun-parrafo cons-sigue">{t('conserv.cuidamos_vacio_p2')}</Typography>
              </>
            ) : (
              <>
                <Typography className="fun-frase">
                  {PREDIOS.length === 1
                    ? t('conserv.predio_uno_' + pais, { ha: HECTAREAS })
                    : t('conserv.predios_n_' + pais, { n: PREDIOS.length, ha: HECTAREAS })}
                </Typography>
                <Box component="ul" className="cons-lista">
                  {PREDIOS.map((x) => (
                    <li key={x.nombre}>
                      <span className="cons-regla-t">{x.nombre}</span> — {t('conserv.predio_linea', { lugar: x.lugar, ha: x.hectareas, via: x.via, desde: x.desde, visita: x.ultimaVisita })}
                    </li>
                  ))}
                </Box>
              </>
            )}
          </Box>
        </Box>

        {/* ⑧ HABLEMOS · DÓNDE PODEMOS — la puerta, y su límite honesto al lado.
            El límite estaba antes escondido en el pie; sacarlo a pilar lo pone a
            la vista de quien está a punto de escribirnos desde otro país, que es
            justo quien necesita leerlo. */}
        {/* `id` para poder enlazar aquí desde fuera. Hoy no lo usa nadie: la
            viñeta de los terrenos de La Fundación se retiró el 1-sep y ese
            camino pasa por «Escríbenos» del cuadro de la Conservación, que va
            directo al formulario de donación. El ancla se queda porque el
            pilar es un destino legítimo y no cuesta nada. */}
        <Box className="fun-seccion fun-dos cons-dos ancla" id="hablemos">
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.hablemos_rotulo')}</Typography>
            <Typography className="fun-frase">{t('conserv.hablemos_frase')}</Typography>
            <Typography className="fun-parrafo">
              {t('conserv.hablemos_texto')}{' '}
              {/* El contacto va DENTRO del párrafo y no debajo: el margen que
                  los separaba eran los 14 px que descuadraban esta fila.

                  ⭐ Y el enlace lleva al formulario de DONACIÓN ya abierto
                  (Faina, 30-ago), no a la lista de motivos: ContactPage guarda
                  el motivo en la URL, así que `?motivo=donar` entra directo.

                  🔴 El correo y el teléfono NO se escriben a mano: salen de
                  `sites.js`, porque en España el número es otro (+34 617 59 84
                  63). Escribir aquí el chileno se lo daría también a quien lee
                  desde España. */}
              <RouterLink to="/contacto?motivo=donar">{t('menu.escribenos')}</RouterLink>.{' '}
              {sitio.email}
              {sitio.whatsapp ? ` · ${sitio.whatsapp.number}` : ''}.
            </Typography>
          </Box>
          <Box className="fun-pilar">
            <Typography className="fun-rotulo">{t('conserv.donde_rotulo')}</Typography>
            <Typography className="fun-frase">{t('conserv.donde_frase')}</Typography>
            <Typography className="fun-parrafo">{t('conserv.donde_texto')}</Typography>
          </Box>
        </Box>

      </Container>

      {/* El bloque «Algunas enseñanzas de la Tierra» (seis mensajes y la firma
          «La Tierra», 1-sep) salio entero el 20-sep (Faina: «vamos a eliminar
          todo lo que dice Algunas enseñanzas de la tierra. TODO»). Los mensajes
          siguen en config/ensenanzas.js como insumo, sin uso en el sitio. */}
    </Box>
  );
};

export default ConservacionPage;
